/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.akdPriceFixQueued = false;
      this.init();
      this.bindEvents();
      this.bindAkdPriceFix();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      if (this.akdPriceFixObserver) {
        this.akdPriceFixObserver.disconnect();
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
        this.scheduleAkdPriceFix();
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      this.scheduleAkdPriceFix();
    }

    bindAkdPriceFix() {
      this.akdPriceFixObserver = new MutationObserver(() => this.scheduleAkdPriceFix());
      this.akdPriceFixObserver.observe(this, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
      this.scheduleAkdPriceFix();
    }

    scheduleAkdPriceFix() {
      if (this.akdPriceFixQueued) return;
      this.akdPriceFixQueued = true;

      window.requestAnimationFrame(() => {
        this.akdPriceFixQueued = false;
        this.correctAkdDealPrices();
      });
    }

    correctAkdDealPrices() {
      this.querySelectorAll('.cart-item[data-product-id="8543302189193"]').forEach((item) => {
        CartDrawer.correctAkdDealCartItem(item);
      });

      this.querySelectorAll('[data-akd-bulk-tshirt-line-price]').forEach((price) => {
        const value = price.dataset.akdBulkTshirtLinePrice;
        if (value && price.textContent.trim() !== value) {
          price.textContent = value;
        }
        price.classList.remove('tpo_cart_item_price');
        price.removeAttribute('tpo-data-key');
      });

      this.querySelectorAll('[data-akd-cart-total]').forEach((total) => {
        const value = total.dataset.akdCartTotal;
        if (value && total.textContent.trim() !== value) {
          total.textContent = value;
        }
      });

      this.querySelectorAll('[data-akd-shirt-qty]').forEach((container) => {
        const rows = CartDrawer.parseAkdShirtQty(container.dataset.akdShirtQtyRaw || container.textContent);
        if (!rows.length) return;

        const html = `<div class="akd-shirt-qty-list" data-akd-shirt-qty-list>${rows.map((row) => (
          `<div class="akd-shirt-qty-row">` +
            `<strong>${CartDrawer.escapeHTML(row.size)}</strong>` +
            `<span>${CartDrawer.escapeHTML(row.qty)}</span>` +
          `</div>`
        )).join('')}</div>`;
        if (container.innerHTML !== html) {
          container.innerHTML = html;
        }
      });
    }

    static correctAkdDealCartItem(item) {
      item.classList.add('akd-bulk-tshirt-drawer-item');
      item.querySelectorAll('.cart-item__total').forEach((total) => {
        total.style.setProperty('display', 'none', 'important');
      });

      CartDrawer.cleanAkdDrawerProperties(item);

      item.querySelectorAll('.akd-shirt-qty-property__value, [data-akd-shirt-qty]').forEach((container) => {
        const rows = CartDrawer.parseAkdShirtQty(container.dataset.akdShirtQtyRaw || container.textContent);
        if (!rows.length) return;

        const html = `<div class="akd-shirt-qty" data-akd-shirt-qty data-akd-shirt-qty-raw="${CartDrawer.escapeHTML(container.dataset.akdShirtQtyRaw || container.textContent)}"><div class="akd-shirt-qty-list" data-akd-shirt-qty-list>${rows.map((row) => (
          `<div class="akd-shirt-qty-row">` +
            `<strong>${CartDrawer.escapeHTML(row.size)}</strong>` +
            `<span>${CartDrawer.escapeHTML(row.qty)}</span>` +
          `</div>`
        )).join('')}</div></div>`;

        if (container.innerHTML !== html) {
          container.innerHTML = html;
        }
      });

      CartDrawer.removeAkdPipeCharacters(item);
    }

    static cleanAkdDrawerProperties(item) {
      item.querySelectorAll('[data-hulkapps-line-properties] > div, .cart-item__variants dl > div').forEach((row) => {
        const label = row.querySelector('dt strong');
        const value = row.querySelector('dd');
        if (!label || !value) return;

        const labelText = label.textContent.replace(':', '').trim();
        const valueText = value.textContent.trim();

        if (labelText === 'Preview' && valueText === 'Preview') {
          row.hidden = true;
          return;
        }

        const drawerLabels = {
          PrintLocations: 'Prints',
          'PrintLocation (1)': 'Location',
          'PrintLocation (2)': 'Location 2',
          'Shirt Quantity': 'Shirt Qty'
        };

        if (drawerLabels[labelText]) label.textContent = `${drawerLabels[labelText]}: `;
      });
    }

    static removeAkdPipeCharacters(container) {
      container.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.includes('|')) node.textContent = node.textContent.replace(/\s*\|\s*/g, ' ');
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE) CartDrawer.removeAkdPipeCharacters(node);
      });
    }

    static parseAkdShirtQty(value) {
      const rawSource = String(value || '').replace(/\r\n/g, '\n').trim();
      const source = rawSource.replace(/\s+/g, ' ').trim();
      if (!source) return [];

      const formattedRows = [];
      source.replace(/([A-Za-z0-9]+)\s*-\s*Qty\s*(\d+)\s*\[\s*([^\]]+)\s*\]/g, (match, size, qty, cost) => {
        formattedRows.push({ size, qty, cost });
        return match;
      });

      if (formattedRows.length) return formattedRows;

      const rows = [];
      let currentRow = null;
        rawSource.split(/\s*\|\s*|\s*,\s*|\n+/).map((part) => part.trim()).filter(Boolean).forEach((part) => {
          const match = part.match(/^(?:(\d+)\s*,\s*)?([A-Za-z0-9]+)\s*(?:-\s*Qty\s*(\d+))?\s*\[\s*\+?([^\]]+)\s*\](?:\s*\[\s*\+?([^\]]+)\s*\])?(?:\s*(\d+))?$/);
          if (match) {
            if (match[1] && currentRow && !currentRow.qty) currentRow.qty = match[1];

          currentRow = {
            size: match[2],
            qty: match[3] || match[6] || '',
            cost: match[5] || match[4]
          };
          rows.push(currentRow);
          return;
        }

          if (/^\d+$/.test(part) && currentRow && !currentRow.qty) {
            currentRow.qty = part;
          }

          const repeatedCompactMatch = part.match(/^([A-Za-z]+)(\d+)\2$/);
          if (repeatedCompactMatch) {
            currentRow = {
              size: repeatedCompactMatch[1],
              qty: repeatedCompactMatch[2],
              cost: ''
            };
            rows.push(currentRow);
            return;
          }

          const simpleMatch = part.match(/^([A-Za-z0-9]+)(?:\s+-\s*Qty\s*|\s+)(\d+)$/);
          if (simpleMatch) {
            currentRow = {
              size: simpleMatch[1],
              qty: simpleMatch[2],
              cost: ''
            };
            rows.push(currentRow);
            return;
          }

          const compactMatch = part.match(/^([A-Za-z]+)(\d+)$/);
          if (compactMatch) {
            currentRow = {
              size: compactMatch[1],
              qty: compactMatch[2],
              cost: ''
            };
            rows.push(currentRow);
            return;
          }

        });

        const filteredRows = rows.filter((row) => row.size && row.qty);
        if (filteredRows.length === 1 && filteredRows[0].qty === '0') filteredRows[0].qty = '100';
        return filteredRows;
      }

    static escapeHTML(value) {
      return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[char]));
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
